// Rückfragen in der App statt Browser-Dialogen (confirm/alert/prompt): gleiche Gestalt wie die übrigen Dialoge,
// modal, schließt nur über Abbrechen, ✕ oder Esc (Enter bestätigt). Aufruf: if (!(await frage('… löschen?', { ja: 'Löschen', gefahr: true }))) return;
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

interface Anfrage {
  text: string;
  titel?: string;
  ja: string;
  /** rot und „Abbrechen“ vorausgewählt (Löschen, Entfernen, Verwerfen) */
  gefahr: boolean;
  /** nur zum Kopieren: zeigt den Wert markiert an, einziger Knopf „Schließen“ */
  wert?: string;
  /** Texteingabe (statt prompt): Feld mit Vorgabe; die Antwort geht als zweiter Wert an `fertig` */
  eingabe?: { vorgabe: string; platzhalter?: string; max?: number };
  fertig: (ok: boolean, text?: string) => void;
}

let setzen: ((a: Anfrage[]) => void) | null = null;
let schlange: Anfrage[] = [];
const aktualisieren = () => setzen?.([...schlange]);

function stellen(a: Omit<Anfrage, 'fertig'>): Promise<boolean> {
  return new Promise((fertig) => {
    schlange.push({ ...a, fertig });
    aktualisieren();
  });
}

/** Ja/Nein-Rückfrage; true = bestätigt. */
export function frage(text: string, o: { ja?: string; titel?: string; gefahr?: boolean } = {}): Promise<boolean> {
  return stellen({ text, titel: o.titel, ja: o.ja ?? 'OK', gefahr: !!o.gefahr });
}

/** Text abfragen (statt prompt); null = abgebrochen, sonst der Text ohne Leerraum am Rand (auch leer). */
export function eingabe(text: string, o: { titel?: string; ja?: string; vorgabe?: string; platzhalter?: string; max?: number } = {}): Promise<string | null> {
  return new Promise((fertig) => {
    schlange.push({ text, titel: o.titel, ja: o.ja ?? 'Speichern', gefahr: false, eingabe: { vorgabe: o.vorgabe ?? '', platzhalter: o.platzhalter, max: o.max },
      fertig: (ok, t) => fertig(ok ? (t ?? '').trim() : null) });
    aktualisieren();
  });
}

/** Rückfrage vor dem Archivieren eines Projekts (Kopfleiste im Projekt, Kontextmenü der Projektkachel). */
export function archivierenBestaetigen(titel: string): Promise<boolean> {
  return frage(`Projekt „${titel}“ archivieren? Es ist danach für alle nur lesbar, wird auf der Startseite ausgeblendet und nach Ablauf der Aufbewahrungsfrist endgültig gelöscht. Bis dahin kann ein Admin es wiederherstellen.`,
    { titel: 'Projekt archivieren', ja: 'Archivieren' });
}

/** Text zum Kopieren anzeigen (z. B. wenn die Zwischenablage gesperrt ist). */
export async function zumKopieren(text: string, wert: string): Promise<void> {
  await stellen({ text, ja: 'Schließen', gefahr: false, wert });
}

/** Einmal in der App einhängen (App.tsx); zeigt die älteste offene Rückfrage. */
export function FragenHost() {
  const [liste, setListe] = useState<Anfrage[]>([]);
  useEffect(() => {
    setzen = setListe;
    aktualisieren();
    return () => { setzen = null; };
  }, []);
  const a = liste[0];
  if (!a) return null;
  return <FrageDialog key={schlange.indexOf(a) + a.text} a={a} />;
}

function FrageDialog({ a }: { a: Anfrage }) {
  const ja = useRef<HTMLButtonElement>(null);
  const nein = useRef<HTMLButtonElement>(null);
  const feld = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(a.eingabe?.vorgabe ?? '');
  const textRef = useRef(text);
  textRef.current = text;
  const antworten = (ok: boolean) => {
    schlange = schlange.filter((x) => x !== a);
    aktualisieren();
    a.fertig(ok, textRef.current);
  };
  useEffect(() => {
    if (a.wert !== undefined || a.eingabe) feld.current?.select();
    else (a.gefahr ? nein : ja).current?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); antworten(a.wert !== undefined); }
      // Enter bestätigt – außer der Fokus liegt auf „Abbrechen“ (dann löst der Knopf selbst aus)
      else if (e.key === 'Enter' && document.activeElement !== nein.current) { e.preventDefault(); antworten(true); }
    };
    window.addEventListener('keydown', taste, true);
    return () => window.removeEventListener('keydown', taste, true);
  }, [a]);
  return (
    <div className="overlay frage-overlay" role="presentation">
      <div className="dialog frage" role="alertdialog" aria-modal="true" aria-label={a.titel ?? 'Rückfrage'} data-testid="frage-dialog">
        <button className="btn icon close" onClick={() => antworten(a.wert !== undefined)} title="Schließen (Esc)"><Icon name="x" /></button>
        {a.titel && <h2>{a.titel}</h2>}
        <p className="frage-text" data-testid="frage-text">{a.text}</p>
        {a.wert !== undefined && <input ref={feld} type="text" readOnly value={a.wert} className="frage-wert" data-testid="frage-wert" onFocus={(e) => e.target.select()} />}
        {a.eingabe && <input ref={feld} type="text" value={text} placeholder={a.eingabe.platzhalter} maxLength={a.eingabe.max} className="frage-wert" data-testid="frage-eingabe"
          onChange={(e) => setText(e.target.value)} />}
        <div className="knoepfe">
          {a.wert === undefined && <button ref={nein} className="btn" onClick={() => antworten(false)} data-testid="frage-nein">Abbrechen</button>}
          <button ref={ja} className={'btn ' + (a.gefahr ? 'danger' : 'primary')} onClick={() => antworten(true)} data-testid="frage-ja">{a.ja}</button>
        </div>
      </div>
    </div>
  );
}
